import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Divider,
  Paper,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { 
  Save, 
  Search,
  ArrowBack,
  // Icon imports
  Fastfood,
  LocalDrink,
  Cake,
  Coffee,
  RestaurantMenu,
  LocalBar,
  EmojiFoodBeverage,
  Icecream,
  LocalPizza,
  LocalDining,
  RamenDining,
  SetMeal,
  LunchDining,
  Restaurant,
  Liquor,
  SoupKitchen,
  BreakfastDining,
  BakeryDining,
  DinnerDining,
  EggAlt,
  KebabDining,
  SportsBar,
  Category
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { colors } from '@mui/material';
import categoryService from '../../services/categoryService';
import SuccessDialog from './dialogs/SuccessDialog';

// Sample icons array - these are what you'll see in the UI
const categoryIcons = [
  { name: 'Fastfood', icon: <Fastfood /> },
  { name: 'Drinks', icon: <LocalDrink /> },
  { name: 'Dessert', icon: <Cake /> },
  { name: 'Coffee', icon: <Coffee /> },
  { name: 'Menu', icon: <RestaurantMenu /> },
  { name: 'Bar', icon: <LocalBar /> },
  { name: 'Tea', icon: <EmojiFoodBeverage /> },
  { name: 'Ice Cream', icon: <Icecream /> },
  { name: 'Pizza', icon: <LocalPizza /> },
  { name: 'Dining', icon: <LocalDining /> },
  { name: 'Ramen', icon: <RamenDining /> },
  { name: 'Sushi', icon: <SetMeal /> },
  { name: 'Lunch', icon: <LunchDining /> },
  { name: 'Restaurant', icon: <Restaurant /> },
  { name: 'Wine', icon: <Liquor /> },
  { name: 'Soup', icon: <SoupKitchen /> },
  { name: 'Breakfast', icon: <BreakfastDining /> },
  { name: 'Bakery', icon: <BakeryDining /> },
  { name: 'Dinner', icon: <DinnerDining /> },
  { name: 'Egg', icon: <EggAlt /> },
  { name: 'Kebab', icon: <KebabDining /> },
  { name: 'Tapas', icon: <SportsBar /> }
];

// Sample category colors
const categoryColors = [
  { name: 'Red', value: colors.red[500] },
  { name: 'Pink', value: colors.pink[500] },
  { name: 'Purple', value: colors.purple[500] },
  { name: 'Deep Purple', value: colors.deepPurple[500] },
  { name: 'Indigo', value: colors.indigo[500] },
  { name: 'Blue', value: colors.blue[500] },
  { name: 'Light Blue', value: colors.lightBlue[500] },
  { name: 'Cyan', value: colors.cyan[500] },
  { name: 'Teal', value: colors.teal[500] },
  { name: 'Green', value: colors.green[500] },
  { name: 'Light Green', value: colors.lightGreen[500] },
  { name: 'Lime', value: colors.lime[500] },
  { name: 'Yellow', value: colors.yellow[600] },
  { name: 'Amber', value: colors.amber[500] },
  { name: 'Orange', value: colors.orange[500] },
  { name: 'Deep Orange', value: colors.deepOrange[500] },
  { name: 'Brown', value: colors.brown[500] },
  { name: 'Grey', value: colors.grey[500] },
  { name: 'Blue Grey', value: colors.blueGrey[500] }
];

const AddCategory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const MotionDiv = motion.div;
  
  // ✅ Form state (No image fields)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    iconName: '',
    colorName: 'Blue',
    colorValue: colors.blue[500]
  });
  
  // ✅ UI states (No image preview states)
  const [iconDialogOpen, setIconDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Handle color selection
  const handleColorChange = (e) => {
    const colorName = e.target.value;
    const colorValue = categoryColors.find(color => color.name === colorName)?.value || colors.blue[500];
    
    setFormData({
      ...formData,
      colorName,
      colorValue
    });
  };
  
  // Dialog management
  const handleIconDialogOpen = () => {
    setIconDialogOpen(true);
  };
  
  const handleIconDialogClose = () => {
    setIconDialogOpen(false);
  };
  
  // Search icons
  const handleIconSearch = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };
  
  // Select an icon
  const handleIconSelect = (iconName) => {
    setFormData({
      ...formData,
      iconName
    });
    setSelectedIcon(iconName);
    setIconDialogOpen(false);
  };
  
  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError(t('category.nameRequired'));
      return false;
    }
    
    return true;
  };
  
  // ✅ Submit form to backend (No image processing)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // ✅ Prepare data for API (No image field)
      const categoryData = {
        name: formData.name,
        description: formData.description || '',
        icon: formData.iconName,
        colorName: formData.colorName,
        colorValue: formData.colorValue,
        image: null // ✅ FIXED: Categories don't use images, set to null
      };
      
      console.log('Sending category data:', categoryData);
      
      // Send to API
      await categoryService.create(categoryData);
      
      // Show success dialog
      setSuccessDialogOpen(true);
      
      // Clear form after successful submission
      setFormData({
        name: '',
        description: '',
        iconName: '',
        colorName: 'Blue',
        colorValue: colors.blue[500]
      });
      
      setSelectedIcon(null);
      
    } catch (err) {
      console.error('Error creating category:', err);
      setError(err.response?.data?.message || t('category.createError'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle success dialog close
  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    navigate('/dashboard/category/list');
  };
  
  // Navigate back to category list
  const handleCancel = () => {
    navigate('/dashboard/category/list');
  };
  
  // Filter icons based on search term
  const filteredIcons = categoryIcons.filter(icon => 
    icon.name.toLowerCase().includes(searchTerm)
  );
  
  // Get selected icon component
  const selectedIconComponent = categoryIcons.find(icon => icon.name === formData.iconName)?.icon || null;
  
  return (
    <Box>
      <MotionDiv
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {t('category.addTitle')}
          </Typography>
          
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleCancel}
          >
            {t('category.backToCategories')}
          </Button>
        </Box>
      </MotionDiv>
      
      {/* Error Alert */}
      {error && (
        <MotionDiv
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </MotionDiv>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('category.information')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        label={t('category.name')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        fullWidth
                        required
                        variant="outlined"
                        error={!!error && !formData.name}
                        helperText={!formData.name && error ? t('category.nameRequired') : ""}
                      />
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={handleIconDialogOpen}
                        sx={{ 
                          height: 56,
                          display: 'flex',
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                          pl: 2,
                          borderColor: formData.iconName ? formData.colorValue : undefined,
                          color: formData.iconName ? formData.colorValue : undefined,
                          '&:hover': {
                            borderColor: formData.iconName ? formData.colorValue : undefined,
                            opacity: 0.8
                          }
                        }}
                      >
                        {formData.iconName ? (
                          <>
                            <Box sx={{ color: formData.colorValue, mr: 1 }}>
                              {selectedIconComponent}
                            </Box>
                            {formData.iconName}
                          </>
                        ) : (
                          <>
                            <Category sx={{ mr: 1, color: 'text.secondary' }} />
                            {t('category.selectIcon')}
                          </>
                        )}
                      </Button>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>{t('category.iconColor')}</InputLabel>
                        <Select
                          name="colorName"
                          value={formData.colorName}
                          onChange={handleColorChange}
                          label={t('category.iconColor')}
                        >
                          {categoryColors.map((color) => (
                            <MenuItem key={color.name} value={color.name}>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box 
                                  sx={{ 
                                    width: 20, 
                                    height: 20, 
                                    borderRadius: '50%', 
                                    bgcolor: color.value,
                                    mr: 1
                                  }} 
                                />
                                {color.name}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <TextField
                        label={t('category.description')}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>
        
        {/* ✅ Preview Section (No image) */}
        <Grid item xs={12} md={4}>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('category.posPreview')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('category.previewDescription')}
                  </Typography>
                  
                  <Paper
                    elevation={3}
                    sx={{ 
                      p: 2, 
                      mt: 2, 
                      borderRadius: 2, 
                      bgcolor: 'background.default' 
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('category.categories')}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 1
                    }}>
                      
                      {/* New category preview */}
                      {(formData.name || formData.iconName) && (
                        <Paper
                          elevation={3}
                          sx={{
                            width: 100,
                            height: 100,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: 2,
                            cursor: 'pointer',
                            borderLeft: `4px solid ${formData.colorValue}`,
                            bgcolor: 'action.hover',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {selectedIconComponent ? (
                            <Box sx={{ color: formData.colorValue, fontSize: 32 }}>
                              {selectedIconComponent}
                            </Box>
                          ) : (
                            <RestaurantMenu sx={{ fontSize: 32, color: formData.colorValue }} />
                          )}
                          <Typography variant="body2" sx={{ mt: 1 }}>
                            {formData.name || t('category.newCategory')}
                          </Typography>
                        </Paper>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </CardContent>
              
              <CardContent sx={{ p: 3, pt: 0 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                  onClick={handleSubmit}
                  disabled={loading || !formData.name}
                  sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
                    }
                  }}
                >
                  {loading ? t('category.saving') : t('category.saveCategory')}
                </Button>
                
                <Button
                  variant="outlined"
                  fullWidth
                  size="large"
                  onClick={handleCancel}
                  sx={{ mt: 2 }}
                  disabled={loading}
                >
                  {t('common.cancel')}
                </Button>
              </CardContent>
            </Card>
          </MotionDiv>
        </Grid>
      </Grid>
      
      {/* Icon Selection Dialog */}
      <Dialog 
        open={iconDialogOpen} 
        onClose={handleIconDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {t('category.selectIconTitle')}
        </DialogTitle>
        
        <DialogContent dividers>
          <TextField
            autoFocus
            margin="dense"
            label={t('category.searchIcons')}
            type="text"
            fullWidth
            variant="outlined"
            value={searchTerm}
            onChange={handleIconSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />
          
          <Grid container spacing={2}>
            {filteredIcons.map((icon) => (
              <Grid item key={icon.name} xs={6} sm={4} md={3}>
                <Paper
                  elevation={selectedIcon === icon.name ? 3 : 1}
                  sx={{
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: selectedIcon === icon.name ? `2px solid ${formData.colorValue}` : '2px solid transparent',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                  onClick={() => handleIconSelect(icon.name)}
                >
                  <Box 
                    sx={{ 
                      color: formData.colorValue, 
                      fontSize: 36,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1
                    }}
                  >
                    {icon.icon}
                  </Box>
                  <Typography variant="body2" align="center">
                    {icon.name}
                  </Typography>
                </Paper>
              </Grid>
            ))}
            
            {filteredIcons.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {t('category.noIconsFound', { searchTerm })}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleIconDialogClose}>{t('common.cancel')}</Button>
          <Button 
            variant="contained"
            onClick={handleIconDialogClose}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a71d6 0%, #6a4292 100%)'
              }
            }}
          >
            {t('common.done')}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success Dialog */}
      <SuccessDialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        message={t('category.createSuccess')}
      />
    </Box>
  );
};

export default AddCategory;